import { Rect } from "@/lib/ai/detection/face-detection"
import { Renderer } from "./pipeline/renderer"
import { PixelData } from "./utils/image-data"
import { Photo } from "./photo"
import { encodeJpeg } from "./encoders/jpeg-encoder"
import { YUVOutput } from "./pipeline/passes/yuv-output-pass"
import { isDeepEqual } from "@/utils/object"
import { getDefaultAdjustments } from "./adjustments"
import { JpegEncoder } from "@/lib/jpeg/jpeg-encoder"

export class CropExtractor {
	private renderer: Renderer

	constructor(renderer: Renderer) {
		this.renderer = renderer
	}

	async cropYUV(
		photo: Photo,
		rects: Rect[],
		signal?: AbortSignal,
		options?: { useAsyncEncode?: boolean },
	): Promise<Blob[]> {
		// Optimization: Sequential reads, concurrent encodes
		// - Reads must be sequential (GPU state, shared framebuffer)
		// - Encodes can run in parallel (worker thread handles them)
		const useAsyncEncode = options?.useAsyncEncode ?? false
		const size = this.renderer.photo.imageState.croppedImageSize
		const width = size[0]
		const height = size[1]

		// Setup YUV textures similar to export-image.ts
		const yTexture = this.renderer.backend.createTexture({
			label: "cropExtractor.yTexture",
			width,
			height,
			format: "r8unorm",
		})

		const uTexture = this.renderer.backend.createTexture({
			label: "cropExtractor.uTexture",
			width: (width + 1) >> 1,
			height: (height + 1) >> 1,
			format: "r8unorm",
		})

		const vTexture = this.renderer.backend.createTexture({
			label: "cropExtractor.vTexture",
			width: (width + 1) >> 1,
			height: (height + 1) >> 1,
			format: "r8unorm",
		})

		const yuvOutput: YUVOutput = {
			y: yTexture,
			u: uTexture,
			v: vTexture,
		}

		try {
			const hasAdjustments = !isDeepEqual(photo.adjustments, getDefaultAdjustments(photo))

			if (!hasAdjustments) {
				this.renderer.renderWithoutAdjustments(photo, {
					colorSpace: "srgb",
					width,
					height,
					output: { format: "yuv", ...yuvOutput },
				})
			} else {
				this.renderer.render(photo, {
					colorSpace: "srgb",
					width,
					height,
					ignoreCrop: false,
					disableHealing: true,
					output: { format: "yuv", ...yuvOutput },
				})
			}

			if (signal?.aborted) {
				return []
			}

			// Phase 1: Sequential reads - collect all pixel data
			const pixelDataList: PixelData[] = []
			let blobs: Blob[] = []

			for (const rect of rects) {
				if (signal?.aborted) {
					return []
				}

				const rectWidth = Math.round(rect.width * width)
				const rectHeight = Math.round(rect.height * height)

				const yWidth = rectWidth
				const yHeight = rectHeight
				const uWidth = (rectWidth + 1) >> 1
				const uHeight = (rectHeight + 1) >> 1
				const yLength = yWidth * yHeight
				const uLength = uWidth * uHeight
				const vLength = uLength
				const totalBytes = yLength + uLength + vLength
				const packedPixels = Math.ceil(totalBytes / 4)
				const packedHeight = Math.max(1, Math.ceil(packedPixels / yWidth))

				const packedTexture = this.renderer.backend.createTexture({
					label: "cropExtractor.packedYuvTexture",
					width: rectWidth,
					height: packedHeight,
					format: "rgba8unorm",
				})

				try {
					yTexture.bind(0)
					uTexture.bind(1)
					vTexture.bind(2)
					this.renderer.setRenderTarget(packedTexture)
					this.renderer.backend.shaders.cropPackedYuv.render({
						rect: [rect.x, rect.y, rect.width, rect.height],
						yTexture: 0,
						uTexture: 1,
						vTexture: 2,
						outputSize: [rectWidth, packedHeight],
						ySize: [yWidth, yHeight],
						uSize: [uWidth, uHeight],
					})

					const packedByteLength = packedTexture.getByteLength()
					const buffer = new ArrayBuffer(packedByteLength)
					const packedView = packedTexture.getBufferView({ buffer })
					const data = packedTexture.read({ bufferView: packedView })

					const imageData: PixelData = {
						data,
						yuvDataLayout: {
							yOffset: 0,
							yLength,
							uOffset: yLength,
							uLength,
							vOffset: yLength + uLength,
							vLength,
						},
						width: rectWidth,
						height: rectHeight,
						colorSpace: "srgb",
					}

					if (!useAsyncEncode) {
						blobs.push(
							await encodeJpeg(imageData, {
								quality: 0.75,
								jpegEncoder: JpegEncoder,
							}),
						)
					} else {
						pixelDataList.push(imageData)
					}
				} finally {
					packedTexture.delete()
				}
			}

			if (signal?.aborted) {
				return []
			}

			// Phase 2: Concurrent encodes - fire all at once, worker handles parallelism
			if (useAsyncEncode) {
				blobs = await Promise.all(
					pixelDataList.map((imageData) =>
						encodeJpeg(imageData, {
							quality: 0.75,
							jpegEncoder: JpegEncoder,
						}),
					),
				)
			}

			return blobs
		} finally {
			yTexture.delete()
			uTexture.delete()
			vTexture.delete()
		}
	}
}
